import React, {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {
    CTA,
    Spinner,
    Text,
    TextAlignment,
    TextTag,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import {Filters} from './components/Filters'
import {Status} from './components/Status'
import {JobAdvertisementsList} from './components/JobAdvertisementsList'
import {getJobAdvertisements} from './utils/getJobAdvertisements'
import {getCategories} from './utils/getCategories'
import {State} from './types/state'
import {JobAdvertisement} from './types/jobAdvertisement'
import {Category} from './types/category'
import {useFeatureServices} from '../use-feature-services'

const SpinnerWrapper = styled('div')`
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
`

const Root = styled.div`
    padding: 0 ${(props) => props.theme.size.grid002};
`

const ClearDealerWrapper = styled('div')`
    display: flex;
    justify-content: center;
    padding-top: ${(props) => props.theme.size.static300};
`

const ListTitle = styled(Text)`
    margin-bottom: ${(props) => props.theme.size.static400};
`

interface PropsApp {
    featureAppId: string
    backendUrl: string
    layout: string
}

export function App(_props: PropsApp): React.JSX.Element {
    const {'mydealer-service': myDealerService} = useFeatureServices() as any

    const [state, setState] = useState<State>({
        categories: [],
        myDealerCode: '',
        jobAdvertisements: [],
        selectedCategories: [],
        loading: true,
        selectedProvinces: [],
    })
    const {t} = useTranslation('fa-posizioni-aperte')

    const jobAds = Array.isArray(state.jobAdvertisements)
        ? state.jobAdvertisements
        : []
    const provinces = Array.from(
        new Set(jobAds.map((jobAdv) => jobAdv.province))
    ).sort((province1, province2) => province1.localeCompare(province2))

    const jobAdvertisementsDisplaying = Array.isArray(state.jobAdvertisements)
        ? state.jobAdvertisements.filter((jobAdvertisement) => {
              const dealerCodeMatches =
                  !state.myDealerCode ||
                  jobAdvertisement.dealer.company_code === state.myDealerCode

              const noSelection =
                  !state.selectedProvinces.length &&
                  !state.selectedCategories.length

              const provinceMatches = state.selectedProvinces.length
                  ? state.selectedProvinces.includes(jobAdvertisement.province)
                  : true

              const jobMatches = state.selectedCategories.length
                  ? state.selectedCategories.includes(
                        jobAdvertisement.category_id
                    )
                  : true

              return (
                  dealerCodeMatches &&
                  (noSelection || (provinceMatches && jobMatches))
              )
          })
        : []

    const listTitleMessage =
        jobAdvertisementsDisplaying.length > 0
            ? 'jobAdvertisementsList.myDealer.title.availablePositions'
            : 'jobAdvertisementsList.myDealer.title.noneNow'

    const myDealerName = jobAdvertisementsDisplaying[0]?.dealer.company_name
        ? jobAdvertisementsDisplaying[0].dealer.company_name
        : t('jobAdvertisementsList.myDealer.title.default')

    const removeFilters = () => {
        setState({
            ...state,
            selectedCategories: [],
            selectedProvinces: [],
        })
    }

    const clearDealer = () => {
        setState((prevState) => ({
            ...prevState,
            myDealerCode: '',
        }))
    }

    useEffect(() => {
        const categories = getCategories(_props.backendUrl)
        const jobAdvertisements = getJobAdvertisements(_props.backendUrl)

        Promise.all([categories, jobAdvertisements]).then((values) => {
            setState((prevState) => ({
                ...prevState,
                categories: values[0] as unknown as Category[],
                jobAdvertisements: values[1] as unknown as JobAdvertisement[],
                loading: false,
            }))
        })
    }, [])

    useEffect(() => {
        if (myDealerService) {
            const myDealerCode = myDealerService.getSelectedDealer()
            console.log('myDealerCode: ' + myDealerCode)

            if (myDealerCode) {
                setState((prevState) => ({
                    ...prevState,
                    myDealerCode: myDealerCode.id,
                }))
            }

            myDealerService.subscribe((dealerId: any) => {
                // tslint:disable-next-line:no-shadowed-variable
                const myDealerCode = dealerId?.id ?? ''
                if (myDealerCode) {
                    setState((prevState) => ({
                        ...prevState,
                        myDealerCode,
                    }))
                } else {
                    setState((prevState) => ({
                        ...prevState,
                        myDealerCode: '',
                        loading: false,
                    }))
                }
            })
        }
    }, [myDealerService])

    return state.loading ? (
        <SpinnerWrapper>
            <Spinner variant="x-large" />
        </SpinnerWrapper>
    ) : _props.layout === 'dealer' &&
      jobAdvertisementsDisplaying.length <= 0 ? (
        <> </>
    ) : state.jobAdvertisements.length > 0 ? (
        <Root>
            {!state.myDealerCode && _props.layout === 'standalone' ? (
                <>
                    <Filters
                        provinces={provinces}
                        setState={setState}
                        state={state}
                    />

                    <Status
                        jobAdvertisementsDisplaying={
                            jobAdvertisementsDisplaying
                        }
                        removeFilters={removeFilters}
                        state={state}
                    />
                </>
            ) : _props.layout === 'standalone' ? (
                <ListTitle
                    tag={TextTag.h3}
                    appearance={TokenTextAppearance.headline400}
                    textAlign={TextAlignment.center}
                >
                    <Trans>{t(listTitleMessage, {myDealerName})}</Trans>
                </ListTitle>
            ) : (
                _props.layout === 'dealer' && (
                    <ListTitle
                        tag={TextTag.h3}
                        appearance={TokenTextAppearance.headline400}
                        textAlign={TextAlignment.center}
                    >
                        <Trans>
                            {' '}
                            {t(
                                'jobAdvertisementsList.myDealer.title.dealerLayoutTitle'
                            )}
                        </Trans>
                    </ListTitle>
                )
            )}

            <JobAdvertisementsList
                categories={state.categories}
                jobAdvertisementsDisplaying={jobAdvertisementsDisplaying}
                myDealerCode={state.myDealerCode}
                removeFilters={removeFilters}
                layout={_props.layout}
            />

            {state.myDealerCode &&
                state.jobAdvertisements.length >
                    jobAdvertisementsDisplaying.length && (
                    <ClearDealerWrapper>
                        <CTA
                            emphasis="secondary"
                            onClick={clearDealer}
                            tag="button"
                        >
                            {t('jobAdvertisementsList.searchEntireNetwork')}
                        </CTA>
                    </ClearDealerWrapper>
                )}
        </Root>
    ) : (
        <Text
            appearance={TokenTextAppearance.headline300}
            textAlign={TextAlignment.center}
        >
            {t('jobAdvertisementsList.noneNow')}
        </Text>
    )
}
